import { useEffect } from 'react';

const buildNoVncSocketUrl = (remoteUrl: string): string | null => {
  try {
    const parsedUrl = new URL(remoteUrl);
    const host = parsedUrl.searchParams.get('host');
    const proxyIp = parsedUrl.searchParams.get('proxyIP');
    if (!host || !proxyIp) {
      return null;
    }

    return `wss://${host}/websockify/${proxyIp}`;
  } catch (error) {
    console.warn('Unable to build noVNC socket url', error);
    return null;
  }
};

export const useNoVncLiveness = ({
  remoteUrl,
  skip,
  isVmReady,
  onAvailable,
}: {
  remoteUrl?: string;
  skip: boolean;
  isVmReady: boolean | undefined;
  onAvailable: () => void;
}): void => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: we do not want to re-run this effect when the onAvailable callback changes
  useEffect(() => {
    if (isVmReady) {
      return undefined;
    }

    if (!remoteUrl) {
      return undefined;
    }

    if (skip) {
      return undefined;
    }

    console.log('Start WebSocket NoVNC Liveness Check');

    const socketUrl = buildNoVncSocketUrl(remoteUrl);
    if (!socketUrl) {
      return undefined;
    }

    let cancelled = false;
    let socket: WebSocket | null = null;
    let retryTimer: number | null = null;

    const clearRetryTimer = () => {
      if (!retryTimer) {
        return;
      }
      clearTimeout(retryTimer);
      retryTimer = null;
    };

    const cleanupSocket = () => {
      if (!socket) {
        return;
      }
      console.log('NoVNC Liveness: Cleaning up socket...');
      socket.onopen = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch (error) {
        console.warn('Unable to close noVNC liveness socket', error);
      }
      socket = null;
    };

    const scheduleRetry = () => {
      if (cancelled) {
        return;
      }
      clearRetryTimer();
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        cleanupSocket();
        attemptConnection();
      }, 5_000);
    };

    const attemptConnection = () => {
      if (cancelled) {
        return;
      }
      cleanupSocket();
      try {
        console.log('NoVNC Liveness: Creating socket...');
        socket = new WebSocket(socketUrl);
      } catch (error) {
        console.warn('Unable to open noVNC liveness socket', error);
        scheduleRetry();
        return;
      }

      scheduleRetry();

      socket.onopen = () => {
        clearRetryTimer();
        cleanupSocket();
        if (cancelled) {
          return;
        }
        cancelled = true;
        console.log('NoVNC Liveness: Socket connection successful');
        onAvailable();
      };
      socket.onerror = () => {
        cleanupSocket();
        scheduleRetry();
      };
      socket.onclose = () => {
        cleanupSocket();
        scheduleRetry();
      };
    };

    attemptConnection();

    return () => {
      cancelled = true;
      clearRetryTimer();
      cleanupSocket();
    };
  }, [remoteUrl, skip, isVmReady]);
};
